import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Loader2, ListChecks, X, Trash2, Check } from "lucide-react";
import Overlay from "./Overlay";
import ReadingFluency from "./ReadingFluency";
import ProgressTracker from "./ProgressTracker";

export default function RunningSection({ studentId }) {
  const [data, setData] = useState(null); // {presets, custom}
  const [records, setRecords] = useState([]);
  const [active, setActive] = useState(null); // template being assessed
  const [known, setKnown] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [newList, setNewList] = useState(null); // {name, category, itemsText}

  const loadAll = () => {
    api.get("/checklists").then((r) => setData(r.data)).catch(() => setData({ presets: [], custom: [] }));
    api.get(`/checklist-records?student_id=${studentId}`).then((r) => setRecords(r.data)).catch(() => {});
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadAll(); }, [studentId]);

  const templates = data ? [...data.presets, ...data.custom] : [];

  const openAssess = (tpl) => {
    setActive(tpl);
    const last = [...records].reverse().find((r) => r.template_id === tpl.id);
    setKnown(new Set(last ? last.known : []));
  };

  const toggle = (item) => setKnown((s) => { const n = new Set(s); n.has(item) ? n.delete(item) : n.add(item); return n; });

  const saveRecord = async () => {
    setSaving(true);
    try {
      await api.post("/checklist-records", {
        student_id: studentId, template_id: active.id, template_name: active.name,
        category: active.category, total: active.items.length, known: Array.from(known),
      });
      toast.success(`${active.name}: ${known.size}/${active.items.length} saved`);
      setActive(null);
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const createList = async () => {
    const items = newList.itemsText.split(/[\n,]+/).map((s) => s.trim()).filter(Boolean);
    if (!newList.name || items.length === 0) { toast.error("Add a name and some items"); return; }
    await api.post("/checklists", { name: newList.name, category: newList.category || "Custom", items });
    toast.success("List created");
    setNewList(null);
    loadAll();
  };

  if (!data) return <div className="flex justify-center py-10 text-slate-300"><Loader2 className="animate-spin" /></div>;

  return (
    <div className="space-y-6">
      <ReadingFluency studentId={studentId} />

      <div className="bg-white rounded-2xl border border-slate-100 shadow-[0_4px_24px_rgba(42,59,71,0.04)] p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-['Outfit'] text-lg font-bold text-slate-800 flex items-center gap-2"><ListChecks size={20} className="text-[#5d8a73]" /> Running assessments</h3>
          <button data-testid="new-list-btn" onClick={() => setNewList({ name: "", category: "", itemsText: "" })}
            className="text-sm font-semibold text-[#5d8a73] hover:underline flex items-center gap-1"><Plus size={15} /> New list</button>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {templates.map((tpl) => {
            const recs = records.filter((r) => r.template_id === tpl.id);
            const last = recs[recs.length - 1];
            return (
              <div key={tpl.id} data-testid={`list-card-${tpl.id}`} className="rounded-xl border border-slate-100 p-4 hover:border-slate-200 transition">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-['Outfit'] font-semibold text-slate-800">{tpl.name}</p>
                    <p className="text-xs text-slate-400">{tpl.category} · {tpl.items.length} items</p>
                  </div>
                  {!tpl.preset && (
                    <button data-testid={`delete-list-${tpl.id}`} onClick={async () => { await api.delete(`/checklists/${tpl.id}`); loadAll(); }} className="text-slate-300 hover:text-red-500"><Trash2 size={14} /></button>
                  )}
                </div>
                {last && (
                  <div className="mt-2">
                    <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
                      <span>Last: {last.score}/{last.total}</span>
                      <span>{Math.round((last.score / last.total) * 100)}%</span>
                    </div>
                    <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-[#81B29A] rounded-full" style={{ width: `${(last.score / last.total) * 100}%` }} />
                    </div>
                  </div>
                )}
                <button data-testid={`assess-list-${tpl.id}`} onClick={() => openAssess(tpl)}
                  className="mt-3 w-full text-sm font-semibold rounded-full py-2 bg-[#4A5D6B] text-white hover:bg-[#3A4A56] transition">
                  {last ? "Re-assess" : "Assess"}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Combined progress tracker */}
      <ProgressTracker records={records} />

      {/* Assess modal */}
      {active && (
        <Overlay onClose={() => setActive(null)}>
          <div className="flex items-center justify-between mb-1">
            <h3 className="font-['Outfit'] text-xl font-bold text-slate-800">{active.name}</h3>
            <button onClick={() => setActive(null)} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
          </div>
          <p className="text-sm text-slate-500 mb-4">Tap each item the student knows — <b className="text-[#5d8a73]">{known.size}/{active.items.length}</b></p>
          <div className="flex flex-wrap gap-2 max-h-[50vh] overflow-y-auto">
            {active.items.map((item) => {
              const on = known.has(item);
              return (
                <button key={item} data-testid={`item-${item}`} onClick={() => toggle(item)}
                  className={`text-sm rounded-lg px-3 py-1.5 border transition ${on ? "bg-[#81B29A] border-[#81B29A] text-white" : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"}`}>
                  {on && <Check size={12} className="inline mr-1" />}{item}
                </button>
              );
            })}
          </div>
          <button data-testid="save-record-btn" onClick={saveRecord} disabled={saving}
            className="mt-5 w-full bg-[#4A5D6B] hover:bg-[#3A4A56] text-white rounded-full py-2.5 font-semibold font-['Outfit'] flex items-center justify-center gap-2 disabled:opacity-60">
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />} Save score ({known.size}/{active.items.length})
          </button>
        </Overlay>
      )}

      {/* New list modal */}
      {newList && (
        <Overlay onClose={() => setNewList(null)}>
          <h3 className="font-['Outfit'] text-xl font-bold text-slate-800 mb-4">New running-assessment list</h3>
          <input data-testid="list-name-input" placeholder="List name (e.g. Tricky Words Term 2)" value={newList.name} onChange={(e) => setNewList({ ...newList, name: e.target.value })}
            className="w-full bg-[#F9F9F7] border border-slate-200 rounded-xl px-3 py-2 mb-3 outline-none focus:border-[#81B29A] focus:bg-white" />
          <input data-testid="list-category-input" placeholder="Category (e.g. Sight Words)" value={newList.category} onChange={(e) => setNewList({ ...newList, category: e.target.value })}
            className="w-full bg-[#F9F9F7] border border-slate-200 rounded-xl px-3 py-2 mb-3 outline-none focus:border-[#81B29A] focus:bg-white" />
          <textarea data-testid="list-items-input" rows={5} placeholder="Items, one per line or comma-separated" value={newList.itemsText} onChange={(e) => setNewList({ ...newList, itemsText: e.target.value })}
            className="w-full bg-[#F9F9F7] border border-slate-200 rounded-xl px-3 py-2 mb-4 outline-none focus:border-[#81B29A] focus:bg-white resize-none" />
          <button data-testid="create-list-btn" onClick={createList} className="w-full bg-[#4A5D6B] hover:bg-[#3A4A56] text-white rounded-full py-2.5 font-semibold font-['Outfit']">Create list</button>
        </Overlay>
      )}
    </div>
  );
}
